/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import FleetService from '../../services/FleetService';

// ─── Palette (matches template) ──────────────────────────────────────────────
const C = {
    MOH_NAVY:   '1A3A5C',
    MOH_BLUE:   '1E5799',
    MOH_LIGHT:  'D6E4F0',
    MOH_MID:    '5B9BD5',
    GREEN:      '2E7D32',
    GREEN_LIGHT:'E8F5E9',
    AMBER:      'B45309',
    AMBER_LIGHT:'FEF3C7',
    RED:        'B91C1C',
    RED_LIGHT:  'FEE2E2',
    GREY_HDR:   'F1F5F9',
    GREY_ALT:   'F8FAFC',
    WHITE:      'FFFFFF',
    GOLD:       'B8860B',
};

// ─── Style helpers ────────────────────────────────────────────────────────────
const fill  = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + hex } });
const font  = (opts) => ({ name: 'Calibri', ...opts });
const align = (h, v = 'middle', wrap = false) => ({ horizontal: h, vertical: v, wrapText: wrap });
const border = (style = 'thin', color = 'FFCCCCCC') => ({
    top: { style, color: { argb: color } }, bottom: { style, color: { argb: color } },
    left: { style, color: { argb: color } }, right: { style, color: { argb: color } },
});

const applyHdr = (row, bg = C.MOH_NAVY, fgHex = C.WHITE, sz = 10) => {
    row.eachCell({ includeEmpty: true }, cell => {
        cell.font      = font({ bold: true, color: { argb: 'FF' + fgHex }, size: sz });
        cell.fill      = fill(bg);
        cell.alignment = align('center');
        cell.border    = border('thin', 'FF999999');
    });
    row.height = 24;
};

const applyDataRow = (row, idx, altBg = C.GREY_ALT) => {
    row.eachCell({ includeEmpty: true }, cell => {
        cell.fill      = fill(idx % 2 === 0 ? C.WHITE : altBg);
        cell.font      = font({ size: 9 });
        cell.alignment = align('left', 'middle');
        cell.border    = border('hair');
    });
    row.height = 16;
};

const titleRow = (ws, text, rowNum, cols) => {
    ws.mergeCells(rowNum, 1, rowNum, cols);
    const c = ws.getCell(rowNum, 1);
    c.value     = text;
    c.font      = font({ bold: true, size: 11, color: { argb: 'FF' + C.MOH_NAVY } });
    c.fill      = fill(C.MOH_LIGHT);
    c.alignment = align('center', 'middle');
    ws.getRow(rowNum).height = 20;
};

const kpiCell = (ws, row, col, label, value, bgHex, textHex) => {
    // Label
    const lc = ws.getCell(row, col);
    lc.value     = label;
    lc.font      = font({ bold: true, size: 9, color: { argb: 'FF' + textHex } });
    lc.fill      = fill(bgHex);
    lc.alignment = align('center', 'middle', true);
    lc.border    = border('medium', 'FF' + C.MOH_BLUE);
    // Value
    const vc = ws.getCell(row + 1, col);
    vc.value     = value;
    vc.font      = font({ bold: true, size: 20, color: { argb: 'FF' + textHex } });
    vc.fill      = fill(bgHex);
    vc.alignment = align('center', 'middle');
    vc.border    = border('medium', 'FF' + C.MOH_BLUE);
    ws.getRow(row).height     = 18;
    ws.getRow(row + 1).height = 32;
};

const fmt    = (v) => v ?? '';
const fmtN   = (v) => (v == null || v === '') ? '' : Number(v);
const fmtD   = (v) => v ? new Date(v).toLocaleDateString('en-GB') : '';
const fmtPct = (v) => v == null ? '' : (Number(v) * 100).toFixed(1) + '%';

// ─── DASHBOARD SHEET ─────────────────────────────────────────────────────────
function buildDashboard(wb, d) {
    const ws = wb.addWorksheet('📊 FLEET DASHBOARD', {
        tabColor: { argb: 'FF' + C.MOH_NAVY },
        views: [{ showGridLines: false }],
    });

    const total = d.total_fleet || 0;

    // helper: horizontal bar using block chars, maxWidth cols
    const bar = (val, maxVal, width = 20) => {
        if (!maxVal) return '';
        const filled = Math.round((val / maxVal) * width);
        return '█'.repeat(Math.max(0, filled)) + '░'.repeat(Math.max(0, width - filled));
    };

    // helper: mini pie slice label
    const pct = (val, tot) => tot > 0 ? ((val / tot) * 100).toFixed(1) + '%' : '0.0%';

    // ── Row 1: Title banner ──
    ws.mergeCells('A1:R1');
    const hdr = ws.getCell('A1');
    hdr.value     = 'MINISTRY OF HEALTH  |  VEHICLE FLEET ASSET REGISTER & DASHBOARD  |  Kingdom of Eswatini';
    hdr.font      = font({ bold: true, size: 13, color: { argb: 'FF' + C.WHITE } });
    hdr.fill      = fill(C.MOH_NAVY);
    hdr.alignment = align('center', 'middle');
    ws.getRow(1).height = 34;

    ws.mergeCells('A2:R2');
    const sub = ws.getCell('A2');
    sub.value     = `Fleet Management & Reporting System  ·  FY 2025/2026  ·  Generated: ${new Date().toLocaleDateString('en-GB', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}`;
    sub.font      = font({ size: 9, italic: true, color: { argb: 'FFADD8E6' } });
    sub.fill      = fill(C.MOH_BLUE);
    sub.alignment = align('center', 'middle');
    ws.getRow(2).height = 16;

    ws.addRow([]); // row 3 spacer

    // ── Row 4-5: KPI Cards (4 cards across B-R) ──
    const functional = d.functional_count || 0;
    const boarded    = (d.by_status || []).filter(s => /board|dispos|grounded/i.test(s.status))
                           .reduce((a, s) => a + Number(s.cnt || 0), 0);
    const utilRate   = d.utilisation_rate != null ? (Number(d.utilisation_rate) * 100).toFixed(1) + '%' : '0.0%';

    const kpis = [
        { col: 2,  label: '🚗  TOTAL FLEET VEHICLES',      val: total,      bg: C.MOH_NAVY, fg: C.WHITE },
        { col: 7,  label: '✅  OPERATIONAL (FUNCTIONAL)',   val: functional, bg: C.GREEN,    fg: C.WHITE },
        { col: 12, label: '🔴  BOARDED / RETIRED',          val: boarded,    bg: C.RED,      fg: C.WHITE },
        { col: 17, label: '📊  FLEET UTILISATION RATE',     val: utilRate,   bg: C.AMBER,    fg: C.WHITE },
    ];

    kpis.forEach(({ col, label, val, bg, fg }) => {
        ws.mergeCells(4, col, 4, col + 4);
        ws.mergeCells(5, col, 5, col + 4);
        const lc = ws.getCell(4, col);
        lc.value     = label;
        lc.font      = font({ bold: true, size: 9, color: { argb: 'FF' + fg } });
        lc.fill      = fill(bg);
        lc.alignment = align('center', 'middle', true);
        lc.border    = border('medium', 'FF' + C.MOH_BLUE);
        ws.getRow(4).height = 20;
        const vc = ws.getCell(5, col);
        vc.value     = val;
        vc.font      = font({ bold: true, size: 22, color: { argb: 'FF' + fg } });
        vc.fill      = fill(bg);
        vc.alignment = align('center', 'middle');
        vc.border    = border('medium', 'FF' + C.MOH_BLUE);
        ws.getRow(5).height = 36;
    });

    ws.addRow([]); // row 6 spacer

    // ══════════════════════════════════════════════════════
    // ROW 7-16: Fleet Status (left) + Vehicle Type (right)
    // ══════════════════════════════════════════════════════

    // ── Status section header ──
    ws.mergeCells('B7:G7');
    const sh = ws.getCell('B7');
    sh.value = '  ⚡  FLEET STATUS BREAKDOWN';
    sh.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    sh.fill  = fill(C.MOH_NAVY);
    sh.alignment = align('left', 'middle');
    ws.getRow(7).height = 20;

    const shHdr = ws.getRow(8);
    ['Status', 'Count', '%', 'Visual Bar (proportion of fleet)'].forEach((v, i) => {
        shHdr.getCell(2 + i).value = v;
    });
    ws.mergeCells(8, 5, 8, 7);
    applyHdr(shHdr, C.MOH_BLUE, C.WHITE, 9);

    const statusRows = d.by_status || [];
    const maxStatusCnt = Math.max(...statusRows.map(s => Number(s.cnt || 0)), 1);
    statusRows.forEach((s, i) => {
        const cnt = Number(s.cnt || 0);
        const r   = ws.getRow(9 + i);
        r.getCell(2).value = s.status;
        r.getCell(3).value = cnt;
        r.getCell(4).value = pct(cnt, total);
        ws.mergeCells(9 + i, 5, 9 + i, 7);
        r.getCell(5).value = bar(cnt, total, 25);
        r.getCell(5).font  = font({ size: 9, color: { argb: /board|dispos|grounded/i.test(s.status) ? 'FF' + C.RED
                                                       : /repair|await/i.test(s.status) ? 'FF' + C.AMBER
                                                       : 'FF' + C.GREEN } });
        applyDataRow(r, i);
        ws.getRow(9 + i).height = 16;
    });
    // Total row
    const stTot = ws.getRow(9 + statusRows.length);
    stTot.getCell(2).value = 'TOTAL';
    stTot.getCell(3).value = total;
    stTot.getCell(4).value = '100.0%';
    stTot.getCell(2).font  = font({ bold: true, size: 9 });
    stTot.getCell(3).font  = font({ bold: true, size: 9 });

    // ── Vehicle Type section header ──
    ws.mergeCells('I7:R7');
    const vth = ws.getCell('I7');
    vth.value = '  🚐  FLEET BY VEHICLE TYPE  (Top 12)';
    vth.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    vth.fill  = fill(C.MOH_NAVY);
    vth.alignment = align('left', 'middle');

    const vtHdr = ws.getRow(8);
    ['Vehicle Type', 'Count', '%', 'Bar'].forEach((v, i) => { vtHdr.getCell(9 + i).value = v; });
    ws.mergeCells(8, 12, 8, 18);

    const topTypes = (d.by_vehicle_type || []).slice(0, 12);
    topTypes.forEach((vt, i) => {
        const cnt = Number(vt.cnt || 0);
        const r   = ws.getRow(8 + 1 + i);
        r.getCell(9).value  = vt.vehicle_type || vt.name;
        r.getCell(10).value = cnt;
        r.getCell(11).value = pct(cnt, total);
        ws.mergeCells(9 + i, 12, 9 + i, 18);
        r.getCell(12).value = bar(cnt, total, 30);
        r.getCell(12).font  = font({ size: 9, color: { argb: 'FF' + C.MOH_BLUE } });
        applyDataRow(r, i);
    });

    ws.addRow([]); // spacer after status/type section

    // ══════════════════════════════════════════════════════
    // Manufacturer + Age Profile side by side
    // ══════════════════════════════════════════════════════
    const mfgStart = 9 + Math.max(statusRows.length + 2, topTypes.length + 1) + 2;

    // ── Manufacturer ──
    ws.mergeCells(mfgStart, 2, mfgStart, 7);
    const mh = ws.getCell(mfgStart, 2);
    mh.value = '  🏭  FLEET BY MANUFACTURER  (Make)';
    mh.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    mh.fill  = fill(C.MOH_NAVY);
    mh.alignment = align('left', 'middle');
    ws.getRow(mfgStart).height = 20;

    const mHdr = ws.getRow(mfgStart + 1);
    ['Manufacturer', 'Total', 'Functional', '% of Fleet', 'Bar'].forEach((v, i) => {
        mHdr.getCell(2 + i).value = v;
    });
    ws.mergeCells(mfgStart + 1, 6, mfgStart + 1, 7);
    applyHdr(mHdr, C.MOH_BLUE, C.WHITE, 9);

    (d.by_manufacturer || []).forEach((m, i) => {
        const cnt  = Number(m.total || m.cnt || 0);
        const func = Number(m.functional || 0);
        const r    = ws.getRow(mfgStart + 2 + i);
        r.getCell(2).value = m.manufacturer || m.name || 'Unknown';
        r.getCell(3).value = cnt;
        r.getCell(4).value = func;
        r.getCell(5).value = pct(cnt, total);
        ws.mergeCells(mfgStart + 2 + i, 6, mfgStart + 2 + i, 7);
        r.getCell(6).value = bar(cnt, total, 18);
        r.getCell(6).font  = font({ size: 9, color: { argb: 'FF' + C.MOH_BLUE } });
        applyDataRow(r, i);
    });

    // ── Age Profile ──
    ws.mergeCells(mfgStart, 9, mfgStart, 18);
    const ah = ws.getCell(mfgStart, 9);
    ah.value = '  📅  FLEET AGE PROFILE';
    ah.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    ah.fill  = fill(C.MOH_NAVY);
    ah.alignment = align('left', 'middle');

    const aHdr = ws.getRow(mfgStart + 1);
    ['Age Bracket', 'Count', '% of Fleet', 'Bar', 'Status Note'].forEach((v, i) => {
        aHdr.getCell(9 + i).value = v;
    });
    ws.mergeCells(mfgStart + 1, 12, mfgStart + 1, 14);
    applyHdr(aHdr, C.MOH_BLUE, C.WHITE, 9);

    const ageColors = { '0-2': C.GREEN, '3-5': '2E7D32', '6-10': C.AMBER, '11-15': 'C05621', '15+': C.RED };
    const ageNotes  = {
        '0-2 Years (New)':       'Excellent condition',
        '3-5 Years':             'Good — routine service',
        '6-10 Years':            'Moderate — monitor closely',
        '11-15 Years':           'Ageing — plan replacement',
        '15+ Years (Aged)':      '⚠ Critical — assess for disposal',
        'Unknown':               'Verify procurement date',
    };

    (d.age_profile || []).forEach((a, i) => {
        const cnt    = Number(a.cnt || 0);
        const ageKey = Object.keys(ageColors).find(k => (a.age_bracket || '').startsWith(k)) || '';
        const color  = ageColors[ageKey] || C.MOH_BLUE;
        const r      = ws.getRow(mfgStart + 2 + i);
        r.getCell(9).value  = a.age_bracket;
        r.getCell(10).value = cnt;
        r.getCell(11).value = pct(cnt, total);
        ws.mergeCells(mfgStart + 2 + i, 12, mfgStart + 2 + i, 14);
        r.getCell(12).value = bar(cnt, total, 20);
        r.getCell(12).font  = font({ size: 9, color: { argb: 'FF' + color } });
        r.getCell(15).value = ageNotes[a.age_bracket] || '';
        r.getCell(15).font  = font({ size: 9, italic: true, color: { argb: 'FF555555' } });
        applyDataRow(r, i);
    });

    // ══════════════════════════════════════════════════════
    // Procurement Trend + Registration Type
    // ══════════════════════════════════════════════════════
    const mfgLen = Math.max((d.by_manufacturer || []).length, (d.age_profile || []).length);
    const ptStart = mfgStart + mfgLen + 3;

    // ── Procurement Trend ──
    ws.mergeCells(ptStart, 2, ptStart, 7);
    const pth = ws.getCell(ptStart, 2);
    pth.value = '  📈  ANNUAL PROCUREMENT TREND';
    pth.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    pth.fill  = fill(C.MOH_NAVY);
    pth.alignment = align('left', 'middle');
    ws.getRow(ptStart).height = 20;

    const ptHdr = ws.getRow(ptStart + 1);
    ['Year', 'Vehicles', 'Cumulative', '% of Total', 'Bar'].forEach((v, i) => {
        ptHdr.getCell(2 + i).value = v;
    });
    ws.mergeCells(ptStart + 1, 6, ptStart + 1, 7);
    applyHdr(ptHdr, C.MOH_BLUE, C.WHITE, 9);

    const maxYrCnt = Math.max(...(d.procurement_trend || []).map(p => Number(p.cnt || 0)), 1);
    let cumulative = 0;
    (d.procurement_trend || []).forEach((pt, i) => {
        const cnt  = Number(pt.cnt || 0);
        cumulative += cnt;                     // ← fixed: numeric addition
        const r    = ws.getRow(ptStart + 2 + i);
        r.getCell(2).value = pt.yr || pt.year;
        r.getCell(3).value = cnt;
        r.getCell(4).value = cumulative;       // ← fixed: actual number
        r.getCell(5).value = pct(cnt, total);
        ws.mergeCells(ptStart + 2 + i, 6, ptStart + 2 + i, 7);
        r.getCell(6).value = bar(cnt, maxYrCnt, 20);  // ← bar relative to peak year
        r.getCell(6).font  = font({ size: 9, color: { argb: 'FF' + C.MOH_BLUE } });
        applyDataRow(r, i);
    });

    // ── Registration Type ──
    ws.mergeCells(ptStart, 9, ptStart, 18);
    const rth = ws.getCell(ptStart, 9);
    rth.value = '  🏷️  FLEET BY REGISTRATION TYPE';
    rth.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    rth.fill  = fill(C.MOH_NAVY);
    rth.alignment = align('left', 'middle');

    const rtHdr = ws.getRow(ptStart + 1);
    ['Type', 'Count', '% of Fleet', 'Visual Proportion'].forEach((v, i) => {
        rtHdr.getCell(9 + i).value = v;
    });
    ws.mergeCells(ptStart + 1, 12, ptStart + 1, 18);
    applyHdr(rtHdr, C.MOH_BLUE, C.WHITE, 9);

    (d.registration_type || []).forEach((rt, i) => {
        const cnt = Number(rt.cnt || 0);
        const r   = ws.getRow(ptStart + 2 + i);
        r.getCell(9).value  = rt.reg_type;
        r.getCell(10).value = cnt;
        r.getCell(11).value = pct(cnt, total);
        ws.mergeCells(ptStart + 2 + i, 12, ptStart + 2 + i, 18);
        r.getCell(12).value = bar(cnt, total, 35);
        r.getCell(12).font  = font({ size: 9, color: { argb: 'FF' + C.MOH_BLUE } });
        applyDataRow(r, i);
    });

    // ── Column widths ──
    [3, 22, 10, 10, 10, 20, 4,  // A-G  (status)
     3, 22, 10, 10, 30, 4, 4, 20, 4, 4, 4]  // H-R  (vehicle type + right panels)
        .forEach((w, i) => { ws.getColumn(i + 1).width = w; });
}


// ─── FLEET REGISTER SHEET ─────────────────────────────────────────────────────
function buildFleetRegister(wb, rows) {
    const ws = wb.addWorksheet('📋 FLEET REGISTER', { tabColor: { argb: 'FF' + C.MOH_BLUE } });

    ws.mergeCells(1, 1, 1, 38);
    const t = ws.getCell(1, 1);
    t.value     = 'MINISTRY OF HEALTH  |  VEHICLE FLEET ASSET REGISTER  |  Kingdom of Eswatini  |  FY 2025/2026  |  CONFIDENTIAL';
    t.font      = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    t.fill      = fill(C.MOH_NAVY);
    t.alignment = align('center', 'middle');
    ws.getRow(1).height = 22;

    const cols = [
        'Registration No.','Private Ref / Old Reg','Make (Manufacturer)','Model','Vehicle Type',
        'Engine Number','Chassis / VIN Number','Year of Manufacture','Date Registered',
        'Registration Year','Fleet Age (Years)','Age Category','Current Status',
        'Operational Centre / Facility','Region','Assigned Programme','Driver / Operator Name',
        'Driver Licence No.','Fuel Type','Engine Capacity (cc)','Colour',
        'Odometer / Mileage (km)','Last Service Date','Next Service Due (km)',
        'Next Service Due (Date)','Insurance Provider','Insurance Expiry Date','Insurance Status',
        'Road Licence Expiry','Road Licence Status','Fitness Certificate Expiry',
        'Condition Rating (1-5)','Maintenance Cost (SZL)','Last Maintenance Notes',
        'Procurement Method','Purchase Price (SZL)','Funded By','Remarks / Notes',
    ];
    const hdr = ws.addRow(cols);
    applyHdr(hdr, C.MOH_NAVY, C.WHITE, 9);
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    const year = new Date().getFullYear();
    (rows || []).forEach((v, i) => {
        const regYear = v.installation_date ? new Date(v.installation_date).getFullYear()
                      : v.procurement_date  ? new Date(v.procurement_date).getFullYear()
                      : null;
        const age      = regYear ? year - regYear : null;
        const ageCat   = !age ? 'Unknown'
                       : age <= 2  ? '0-2 Years (New)'
                       : age <= 5  ? '3-5 Years'
                       : age <= 10 ? '6-10 Years'
                       : age <= 15 ? '11-15 Years'
                       : '15+ Years (Aged)';
        const r = ws.addRow([
            fmt(v.registration_no),  fmt(v.private_ref_old_reg),
            fmt(v.manufacturer),     fmt(v.model),
            fmt(v.vehicle_type),     fmt(v.engine_no || v.eieftnumber),
            fmt(v.chassis_no || v.serial_no),
            regYear || '',           fmtD(v.installation_date || v.procurement_date),
            regYear || '',           age != null ? age : '',
            ageCat,                  fmt(v.functional_status),
            fmt(v.facility),         fmt(v.region),
            fmt(v.assigned_programme), fmt(v.assigned_driver),
            '',                      'Diesel',
            fmt(v.engine_capacity_cc), fmt(v.colour),
            fmtN(v.current_odometer),  '',
            '',                      '',
            '',                      'Pending',
            '',                      'Pending',
            '',                      'Pending',
            fmt(v.condition_rating ? v.condition_rating + '/5' : ''),
            '',                      '',
            fmt(v.procurement_method), fmtN(v.cost),
            fmt(v.funded_by),        fmt(v.eicomment),
        ]);
        applyDataRow(r, i);
        // Colour status cell
        const stCell = r.getCell(13);
        if (/board|dispos/i.test(v.functional_status)) {
            stCell.font = font({ bold: true, size: 9, color: { argb: 'FF' + C.RED } });
        } else if (/repair|await/i.test(v.functional_status)) {
            stCell.font = font({ bold: true, size: 9, color: { argb: 'FF' + C.AMBER } });
        } else if (/function|good|fair/i.test(v.functional_status)) {
            stCell.font = font({ bold: true, size: 9, color: { argb: 'FF' + C.GREEN } });
        }
    });

    const widths = [14,14,18,14,16,16,20,8,14,8,8,16,18,24,12,18,20,14,10,10,10,12,12,12,12,16,14,12,14,12,14,10,14,22,16,12,18,22];
    widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.autoFilter = { from: 'A2', to: ws.getCell(2, cols.length).address };
}

// ─── MAINTENANCE LOG ──────────────────────────────────────────────────────────
function buildMaintenance(wb, rows) {
    const ws = wb.addWorksheet('🔧 MAINTENANCE LOG', { tabColor: { argb: 'FF' + C.AMBER } });

    ws.mergeCells(1, 1, 1, 17);
    const t = ws.getCell(1, 1);
    t.value = 'MINISTRY OF HEALTH  |  VEHICLE MAINTENANCE LOG  |  Kingdom of Eswatini';
    t.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    t.fill  = fill(C.AMBER);
    t.alignment = align('center');
    ws.getRow(1).height = 20;

    const cols = ['Log ID (Auto)','Registration No.','Make / Model','Service Date','Odometer at Service (km)',
        'Service Type','Service Provider / Garage','Cost (SZL)','Parts Replaced','Next Service (km)',
        'Next Service Date','Performed By','Approved By','Job Card / Invoice No.','Warranty / Guarantee','Status','Notes'];
    const hdr = ws.addRow(cols);
    applyHdr(hdr, C.AMBER, C.WHITE, 9);
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    (rows || []).forEach((v, i) => {
        const r = ws.addRow([
            i + 1, fmt(v.registration_no), fmt(v.vehicle_name || v.vehicle_type),
            fmtD(v.cost_date), fmtN(v.odometer_at_service), fmt(v.service_type || v.cost_type),
            fmt(v.service_provider || v.recorded_by_name), fmtN(v.amount || v.cost),
            fmt(v.parts_replaced), '', '', fmt(v.performed_by || v.recorded_by_name),
            fmt(v.approved_by_name), fmt(v.job_card_no), fmt(v.warranty_info),
            fmt(v.status || 'Completed'), fmt(v.notes),
        ]);
        applyDataRow(r, i);
    });

    if (!rows || rows.length === 0) {
        const blankCols = cols.length;
        for (let i = 0; i < 20; i++) {
            const r = ws.addRow(Array(blankCols).fill(''));
            applyDataRow(r, i);
        }
    }

    [8,14,18,12,14,18,20,12,20,12,12,16,16,16,16,12,24].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.autoFilter = { from: 'A2', to: ws.getCell(2, cols.length).address };
}

// ─── FUEL LOG ────────────────────────────────────────────────────────────────
function buildFuel(wb, rows) {
    const ws = wb.addWorksheet('⛽ FUEL LOG', { tabColor: { argb: 'FF2E7D32' } });

    ws.mergeCells(1, 1, 1, 14);
    const t = ws.getCell(1, 1);
    t.value = 'MINISTRY OF HEALTH  |  VEHICLE FUEL CONSUMPTION LOG  |  Kingdom of Eswatini';
    t.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    t.fill  = fill(C.GREEN);
    t.alignment = align('center');
    ws.getRow(1).height = 20;

    const cols = ['Log ID','Date','Registration No.','Driver Name','Odometer Reading (km)',
        'Litres Filled','Fuel Type','Cost per Litre (SZL)','Total Cost (SZL)',
        'Fuel Station / Supplier','Authorised By','Receipt / Invoice No.','Consumption (km/L)','Remarks'];
    const hdr = ws.addRow(cols);
    applyHdr(hdr, C.GREEN, C.WHITE, 9);
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    (rows || []).forEach((v, i) => {
        const r = ws.addRow([
            i + 1, fmtD(v.month || v.trip_date), fmt(v.registration_no),
            fmt(v.driver_name), fmtN(v.km_driven),
            fmtN(v.total_litres), 'Diesel', '',
            fmtN(v.total_cost), '', '', '',
            fmtN(v.litres_per_100km), '',
        ]);
        applyDataRow(r, i);
    });

    if (!rows || rows.length === 0) {
        for (let i = 0; i < 30; i++) {
            const r = ws.addRow(Array(cols.length).fill(''));
            applyDataRow(r, i);
        }
    }

    [8,12,14,18,14,12,10,12,12,18,16,16,12,20].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.autoFilter = { from: 'A2', to: ws.getCell(2, cols.length).address };
}

// ─── INCIDENT LOG ────────────────────────────────────────────────────────────
function buildIncidents(wb, rows) {
    const ws = wb.addWorksheet('🚨 INCIDENT LOG', { tabColor: { argb: 'FF' + C.RED } });

    ws.mergeCells(1, 1, 1, 20);
    const t = ws.getCell(1, 1);
    t.value = 'MINISTRY OF HEALTH  |  VEHICLE INCIDENT & ACCIDENT LOG  |  Kingdom of Eswatini';
    t.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    t.fill  = fill(C.RED);
    t.alignment = align('center');
    ws.getRow(1).height = 20;

    const cols = ['Incident ID','Date of Incident','Time','Registration No.','Driver Name',
        'Location / Region','Incident Type','Description','Third Party Involved?','Police Report No.',
        'Injuries Reported','Damage Assessment','Repair Cost (SZL)','Insurance Claim No.',
        'Claim Status','Action Taken','Date Resolved','Reported By','Approved By','Remarks'];
    const hdr = ws.addRow(cols);
    applyHdr(hdr, C.RED, C.WHITE, 9);
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    (rows || []).forEach((v, i) => {
        const r = ws.addRow([
            v.id, fmtD(v.incident_date), fmt(v.incident_time),
            fmt(v.registration_no), fmt(v.driver_name),
            [v.location, v.region].filter(Boolean).join(' / '),
            fmt(v.incident_type), fmt(v.description),
            v.third_party == 1 ? 'Yes' : 'No', fmt(v.police_report_no),
            fmt(v.injuries_reported), fmt(v.damage_assessment),
            fmtN(v.repair_cost), fmt(v.insurance_claim_no),
            fmt(v.claim_status), fmt(v.action_taken),
            fmtD(v.date_resolved), fmt(v.reported_by_name),
            fmt(v.approved_by_name), fmt(v.notes),
        ]);
        applyDataRow(r, i);
        // Severity colour on type column
        const sevCell = r.getCell(7);
        if (/accident/i.test(v.incident_type)) {
            sevCell.font = font({ bold: true, size: 9, color: { argb: 'FF' + C.RED } });
        }
    });

    if (!rows || rows.length === 0) {
        for (let i = 0; i < 10; i++) {
            const r = ws.addRow(Array(cols.length).fill(''));
            applyDataRow(r, i);
        }
    }

    [10,12,8,14,18,20,18,30,10,14,16,22,12,16,12,24,12,16,16,22].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.autoFilter = { from: 'A2', to: ws.getCell(2, cols.length).address };
}

// ─── DISPOSAL REGISTER ───────────────────────────────────────────────────────
function buildDisposals(wb, rows) {
    const ws = wb.addWorksheet('📦 DISPOSAL REGISTER', { tabColor: { argb: 'FF555555' } });

    ws.mergeCells(1, 1, 1, 20);
    const t = ws.getCell(1, 1);
    t.value = 'MINISTRY OF HEALTH  |  VEHICLE DISPOSAL & BOARDING REGISTER  |  Kingdom of Eswatini';
    t.font  = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    t.fill  = fill('333333');
    t.alignment = align('center');
    ws.getRow(1).height = 20;

    const cols = ['Disposal ID','Registration No.','Make / Model','Year of Manufacture',
        'Date of Acquisition','Date Boarded','Reason for Disposal','Condition at Disposal',
        'Mileage at Disposal (km)','Board Authority Ref','Minutes of Meeting Ref',
        'Disposal Method','Sale/Disposal Price (SZL)','Proceeds to Government',
        'Replacement Vehicle Reg','Approved By (HOD)','Approved By (Director)',
        'Date of Actual Disposal','Receiving Entity','Remarks'];
    const hdr = ws.addRow(cols);
    applyHdr(hdr, '333333', C.WHITE, 9);
    ws.views = [{ state: 'frozen', ySplit: 2 }];

    (rows || []).forEach((v, i) => {
        const r = ws.addRow([
            v.id || (i + 1),
            fmt(v.registration_no),
            [v.manufacturer, v.vehicle_type].filter(Boolean).join(' ') || 'Boarded — Pending Assessment',
            v.year_of_manufacture || '',
            fmtD(v.date_of_acquisition || v.procurement_date),
            fmtD(v.created),
            fmt(v.reason) || '',
            fmt(v.condition_at_disposal) || 'Boarded — Pending Assessment',
            fmtN(v.mileage_at_disposal || v.current_odometer),
            fmt(v.board_authority_ref),
            fmt(v.minutes_ref) || 'Pending Disposal',
            fmt(v.disposal_method),
            fmtN(v.sale_price),
            fmtN(v.proceeds_to_govt),
            fmt(v.replacement_vehicle_reg),
            fmt(v.hod_approver_name),
            fmt(v.director_approver_name),
            fmtD(v.date_actual_disposal || v.disposal_date),
            fmt(v.receiving_entity),
            fmt(v.notes),
        ]);
        applyDataRow(r, i);
    });

    [10,14,22,10,14,14,22,22,14,16,16,18,14,14,16,18,20,14,18,22].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.autoFilter = { from: 'A2', to: ws.getCell(2, cols.length).address };
}

// ─── LOOKUP TABLES ────────────────────────────────────────────────────────────
function buildLookups(wb, lookups) {
    const ws = wb.addWorksheet('🔖 LOOKUP TABLES', { tabColor: { argb: 'FF888888' } });

    ws.mergeCells('A1:H1');
    ws.getCell('A1').value     = 'LOOKUP REFERENCE TABLES — Do not modify unless authorised';
    ws.getCell('A1').font      = font({ bold: true, size: 10, color: { argb: 'FF' + C.WHITE } });
    ws.getCell('A1').fill      = fill('555555');
    ws.getCell('A1').alignment = align('center');
    ws.getRow(1).height = 20;

    ws.addRow([]); // spacer

    const keys   = Object.keys(lookups || {});
    const labels = keys.map(k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    const hdr    = ws.addRow(labels);
    applyHdr(hdr, C.MOH_NAVY, C.WHITE, 9);

    const maxLen = Math.max(...keys.map(k => (lookups[k] || []).length));
    for (let row = 0; row < maxLen; row++) {
        const rowData = keys.map(k => (lookups[k] || [])[row] ?? '');
        const r = ws.addRow(rowData);
        applyDataRow(r, row);
    }

    keys.forEach((k, i) => { ws.getColumn(i + 1).width = Math.max(labels[i].length + 4, 20); });
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FleetExport({ buttonStyle = {} }) {
    const [loading, setLoading] = useState(false);

    const doExport = async () => {
        setLoading(true);
        try {
            const res = await FleetService.getExportData();
            if (Number(res?.data?.code) !== 200) {
                message.error(res?.data?.message || 'Failed to fetch export data');
                return;
            }
            const { dashboard, register, maintenance, fuel, incidents, disposals, lookups } = res.data.result;

            const wb       = new ExcelJS.Workbook();
            wb.creator     = 'MoH Fleet VMS';
            wb.created     = new Date();
            wb.modified    = new Date();
            wb.company     = 'Ministry of Health — Kingdom of Eswatini';

            buildDashboard(wb, dashboard);
            buildFleetRegister(wb, register);
            buildMaintenance(wb, maintenance);
            buildFuel(wb, fuel);
            buildIncidents(wb, incidents);
            buildDisposals(wb, disposals);
            buildLookups(wb, lookups);

            const buf      = await wb.xlsx.writeBuffer();
            const dateStr  = new Date().toISOString().slice(0, 10);
            saveAs(new Blob([buf]), `MoH_Fleet_Register_${dateStr}.xlsx`);
            message.success('Fleet register exported successfully');

        } catch (err) {
            console.error('Fleet export error:', err);
            message.error('Export failed — ' + (err.message || 'unknown error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Tooltip title="Export fleet register to Excel — matches template format">
            <Button
                icon={<FileExcelOutlined />}
                loading={loading}
                onClick={doExport}
                style={{
                    background: '#217346',
                    borderColor: '#217346',
                    color: '#fff',
                    fontWeight: 500,
                    ...buttonStyle,
                }}
            >
                {loading ? 'Exporting…' : 'Export Excel'}
            </Button>
        </Tooltip>
    );
}